<template>
  <div class="purchase-details">
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 5">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Supplier</label>
          <FormDropdown v-model="filters.supplier" :options="supplierOptions" placeholder="All suppliers" clearable />
        </div>
        <div class="rp-filter">
          <label>Warehouse</label>
          <FormDropdown v-model="filters.warehouse" :options="warehouseOptions" placeholder="All warehouses" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Item / Invoice</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control"
                   placeholder="Search item, code, SKU, invoice or chalan" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-shopping-cart"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Purchased</div>
          <div class="rp-card-value">{{ formatMoney(summary.total) }}</div>
          <div class="rp-card-sub">{{ formatQty(summary.qty) }} units · {{ plural(summary.line_count, 'line') }}</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-tag"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Avg Unit Cost</div>
          <div class="rp-card-value">{{ formatMoney(summary.avg_unit_cost) }}</div>
          <div class="rp-card-sub">
            {{ plural(summary.product_count, 'product') }} from {{ plural(summary.supplier_count, 'supplier') }}
          </div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-trending-up"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Markup on Cost</div>
          <div class="rp-card-value">{{ summary.markup === null ? '—' : `${summary.markup}%` }}</div>
          <div class="rp-card-sub">
            discount {{ formatMoney(summary.discount) }} ({{ summary.discount_rate }}%) · VAT {{ formatMoney(summary.vat) }}
          </div>
        </div>
      </div>
      <div class="rp-card tone-teal">
        <span class="rp-card-icon"><i class="ti ti-truck-return"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Returned</div>
          <div class="rp-card-value">{{ formatMoney(summary.returned_value) }}</div>
          <div class="rp-card-sub">{{ formatQty(summary.returned_qty) }} units sent back since</div>
        </div>
      </div>
    </div>

    <div class="rp-insights" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title">Purchases by category</div>
        <div v-if="categoryBars.length" class="rp-bars">
          <div v-for="category in categoryBars" :key="category.name">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">{{ category.name }}</span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(category.total) }} <span class="text-muted fw-normal">· {{ formatQty(category.qty) }}</span>
              </span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill info" :style="{ width: `${category.percent}%` }"></div></div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No purchases in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Top products</div>
        <ul v-if="summary.top_products.length" class="rp-ranked">
          <li v-for="(item, index) in summary.top_products.slice(0, 6)" :key="item.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="item.name">{{ item.name }}</div>
              <small class="text-muted">{{ formatQty(item.qty) }} received</small>
            </div>
            <span class="fw-semibold text-nowrap">{{ formatMoney(item.total) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No purchases in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">By supplier</div>
        <div v-if="supplierBars.length" class="rp-bars">
          <div v-for="supplier in supplierBars" :key="supplier.name">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">{{ supplier.name }}</span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(supplier.total) }} <span class="text-muted fw-normal">({{ supplier.share }}%)</span>
              </span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :style="{ width: `${supplier.percent}%` }"></div></div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No purchases in this period.</p>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="fw-semibold text-body">
          <i class="ti ti-list-details me-1"></i>Invoice lines
          <span class="rp-pill-count ms-1">{{ formatInt(rows.length) }}</span>
          <span v-if="sortKey && sortKey !== 'invoice_no'" class="rp-hint ms-2">
            Sorted by {{ sortLabel }} — <a href="#" @click.prevent="clearSort">group by invoice</a>
          </span>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}<i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="{ row, span, group } in pagedLines" :key="row.key" :class="{ 'pd-group-start': span }">
              <td v-if="span" :rowspan="span" class="pd-invoice">
                <div class="fw-semibold text-nowrap">{{ row.invoice_no }}</div>
                <span class="rp-sub">{{ formatDate(row.date) }}</span>
                <div class="pd-supplier text-truncate" :title="row.supplier">
                  <i class="ti ti-truck me-1"></i>{{ row.supplier }}
                </div>
                <span v-if="group.lines > 1" class="rp-sub">{{ plural(group.lines, 'line') }} · {{ formatMoney(group.total) }}</span>
              </td>
              <td>
                <div class="fw-medium">
                  {{ row.item_name }}
                  <span v-if="Number(row.returned_qty)" class="rp-badge info ms-1"
                        :title="`${formatMoney(row.returned_value)} returned to the supplier`">
                    <i class="ti ti-arrow-back-up"></i>{{ formatQty(row.returned_qty) }} returned
                  </span>
                </div>
                <span class="rp-sub">
                  {{ row.item_code }}<template v-if="row.warehouse_id"> · {{ row.warehouse }}</template>
                </span>
                <span v-for="part in variantParts(row)" :key="part" class="rp-variant mt-1">{{ part }}</span>
              </td>
              <td>
                <div class="text-nowrap">{{ row.batch_no || '—' }}</div>
                <span v-if="row.chalan_no" class="rp-sub">Chalan {{ row.chalan_no }}</span>
                <span v-if="row.expiry_date" class="rp-sub">Exp {{ formatDate(row.expiry_date) }}</span>
              </td>
              <td class="text-end fw-semibold">
                {{ formatQty(row.qty) }}<small v-if="row.unit" class="text-muted ms-1">{{ row.unit }}</small>
              </td>
              <td class="text-end">{{ formatMoney(row.unit_price) }}</td>
              <td class="text-end">{{ formatMoney(row.cost) }}</td>
              <td class="text-end">
                <template v-if="Number(row.discount)">
                  −{{ formatMoney(row.discount) }}
                  <span class="rp-sub">{{ row.discount_pct }}%</span>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-end" :class="{ 'text-muted': !Number(row.vat) }">
                {{ Number(row.vat) ? formatMoney(row.vat) : '—' }}
              </td>
              <td class="text-end fw-semibold">{{ formatMoney(row.total) }}</td>
              <td class="text-end">
                <template v-if="row.markup !== null">
                  {{ formatMoney(row.selling_price) }}
                  <span class="rp-sub" :class="markupClass(row.markup)">{{ row.markup }}% markup</span>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>

            <tr v-if="!loading && !pagedLines.length">
              <td :colspan="columns.length">
                <div class="rp-empty">
                  <i class="ti ti-list-details"></i>
                  <div class="fw-semibold">No purchases found</div>
                  <small class="text-muted">Try another period, supplier or search.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th colspan="3" class="text-end">
                Total ({{ plural(summary.invoice_count, 'invoice') }} · {{ plural(rows.length, 'line') }})
              </th>
              <th class="text-end">{{ formatQty(summary.qty) }}</th>
              <th class="text-end">{{ formatMoney(summary.avg_unit_cost) }}</th>
              <th class="text-end">{{ formatMoney(summary.cost) }}</th>
              <th class="text-end">{{ Number(summary.discount) ? `−${formatMoney(summary.discount)}` : '—' }}</th>
              <th class="text-end">{{ formatMoney(summary.vat) }}</th>
              <th class="text-end">{{ formatMoney(summary.total) }}</th>
              <th class="text-end">{{ summary.markup === null ? '' : `${summary.markup}%` }}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination v-if="visibleRows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="visibleRows.length" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Unit price = cost ÷ quantity received. Total = cost − discount + VAT. Markup compares the batch selling price
      with the purchase price; returned shows what has gone back to the supplier since.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { marginTone } from '../sales/salesReportMixin';
import purchaseReport from './purchaseReportMixin';

const EMPTY_SUMMARY = {
  qty: 0, cost: 0, discount: 0, vat: 0, total: 0, returned_qty: 0, returned_value: 0, line_count: 0,
  invoice_count: 0, product_count: 0, supplier_count: 0, avg_unit_cost: 0, discount_rate: 0, markup: null,
  top_products: [], by_category: [], by_supplier: [],
};

export default {
  name: 'PurchaseDetailsReport',
  mixins: [inventoryReport, periodReport, salesReport, purchaseReport],

  reportUrl: apiUrls.purchase_details_report.get,
  fileName: 'purchase_details',
  columns: [
    { key: 'invoice_no', label: 'Invoice' },
    { key: 'item_name', label: 'Product' },
    { key: 'batch_no', label: 'Batch' },
    { key: 'qty', label: 'Qty', class: 'text-end', numeric: true },
    { key: 'unit_price', label: 'Unit Price', class: 'text-end', numeric: true },
    { key: 'cost', label: 'Cost', class: 'text-end', numeric: true },
    { key: 'discount', label: 'Discount', class: 'text-end', numeric: true },
    { key: 'vat', label: 'VAT', class: 'text-end', numeric: true },
    { key: 'total', label: 'Total', class: 'text-end', numeric: true },
    { key: 'markup', label: 'Sell Price', class: 'text-end', numeric: true },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), supplier: null, warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
    };
  },

  computed: {
    hasActiveFilters() {
      return this.periodChanged || this.itemFiltersActive || !!this.filters.supplier;
    },

    categoryBars() {
      return this.withPercent(this.summary.by_category, 'total');
    },

    supplierBars() {
      const total = Number(this.summary.total) || 0;
      return this.withPercent(this.summary.by_supplier, 'total').map((s) => ({
        ...s, share: total ? Math.round(((Number(s.total) || 0) / total) * 100) : 0,
      }));
    },

    sortLabel() {
      return (this.columns.find((c) => c.key === this.sortKey)?.label || '').toLowerCase();
    },

    visibleRows() {
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },

    /** The page's rows, each with how many following rows (itself included) belong to the same invoice. */
    pagedLines() {
      const rows = this.pagedRows;
      const groups = {};
      this.visibleRows.forEach((row) => {
        const group = groups[row.invoice_no] || (groups[row.invoice_no] = { lines: 0, total: 0 });
        group.lines += 1;
        group.total += Number(row.total) || 0;
      });
      return rows.map((row, index) => {
        if (index && rows[index - 1].invoice_no === row.invoice_no) return { row, span: 0 };
        let span = 1;
        while (rows[index + span]?.invoice_no === row.invoice_no) span += 1;
        return { row, span, group: groups[row.invoice_no] };
      });
    },
  },

  methods: {
    extraParams() {
      return { ...this.periodParams(), ...this.supplierParams() };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...defaultPeriod(this.today), supplier: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
    },

    clearSort() {
      this.sortKey = null;
      this.page = 1;
    },

    variantParts(row) {
      return row.variant ? row.variant.split(' · ') : [];
    },

    markupClass(markup) {
      return { danger: 'text-danger', warning: 'text-warning', success: 'text-success' }[marginTone(markup)];
    },

    printReport() {
      const s = this.summary;
      this.printColumns(
        'Purchase Details',
        [this.periodText(), this.supplierText(), ...this.itemFiltersText()],
        [
          ['Purchased', `${this.formatMoney(s.total)} · ${this.formatQty(s.qty)} units`],
          ['Avg Unit Cost', this.formatMoney(s.avg_unit_cost)],
          ['Discount · VAT', `${this.formatMoney(s.discount)} · ${this.formatMoney(s.vat)}`],
          ['Returned', `${this.formatQty(s.returned_qty)} · ${this.formatMoney(s.returned_value)}`],
        ],
        [
          { label: 'Date', value: (r) => this.formatDate(r.date) },
          { label: 'Invoice', value: (r) => r.invoice_no },
          { label: 'Supplier', value: (r) => r.supplier },
          { label: 'Product', value: (r) => `${r.item_code} · ${r.item_name}${r.variant ? ` (${r.variant})` : ''}` },
          { label: 'Batch', value: (r) => r.batch_no },
          { label: 'Qty', value: (r) => this.formatQty(r.qty), num: true },
          { label: 'Unit Price', value: (r) => this.formatMoney(r.unit_price), num: true },
          { label: 'Cost', value: (r) => this.formatMoney(r.cost), num: true },
          { label: 'Discount', value: (r) => this.formatMoney(r.discount), num: true },
          { label: 'VAT', value: (r) => this.formatMoney(r.vat), num: true },
          { label: 'Total', value: (r) => this.formatMoney(r.total), num: true },
          { label: 'Returned', value: (r) => this.formatQty(r.returned_qty), num: true },
        ],
        this.visibleRows,
        {
          Batch: 'Total', Qty: this.formatQty(s.qty), Cost: this.formatMoney(s.cost),
          Discount: this.formatMoney(s.discount), VAT: this.formatMoney(s.vat), Total: this.formatMoney(s.total),
          Returned: this.formatQty(s.returned_qty),
        },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>

<style scoped>
.pd-invoice {
  width: 190px;
  max-width: 220px;
  border-right: 1px solid #F0F2F5;
  background: #FCFCFD;
  vertical-align: top !important;
}

.pd-supplier {
  margin-top: 0.35rem;
  color: #3A4652;
  font-size: 0.8rem;
}

.rp-table > tbody > tr.pd-group-start > td {
  border-top: 1px solid #E6EAED;
}
</style>
